import { useEffect, useState } from 'react'
import { Loader2, LayoutTemplate } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useOutlineTemplatesStore } from '@/stores/outline-templates-store'
import type { OutlineTemplate, OutlineTemplateSection } from '@/lib/api'

interface TemplatePickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentUserId: string
  onSelect: (sections: OutlineTemplateSection[]) => void
}

export function TemplatePickerDialog({
  open,
  onOpenChange,
  currentUserId,
  onSelect,
}: TemplatePickerDialogProps) {
  const { templates, isLoading, fetchTemplates } = useOutlineTemplatesStore()
  const [selected, setSelected] = useState<OutlineTemplate | null>(null)

  useEffect(() => {
    if (open) {
      void fetchTemplates()
      setSelected(null)
    }
  }, [open, fetchTemplates])

  const handleApply = () => {
    if (!selected) return
    onSelect(selected.sections)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Start from Template</DialogTitle>
          <DialogDescription>
            Choose a template to populate your outline with predefined sections.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 px-1 -mx-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <LayoutTemplate className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="text-sm font-medium">No templates yet</p>
              <p className="text-meta mt-1">
                Create a template from the Templates page first.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {templates.map((template) => {
                const isCreator = template.createdBy === currentUserId
                const isSelected = selected?.id === template.id

                return (
                  <button
                    key={template.id}
                    className={`text-left p-3 rounded-lg border transition-colors ${
                      isSelected
                        ? 'border-foreground bg-muted/50'
                        : 'border-border hover:border-foreground/20'
                    }`}
                    onClick={() => setSelected(isSelected ? null : template)}
                  >
                    <p className="text-sm font-medium truncate">{template.name}</p>
                    {template.description && (
                      <p className="text-meta mt-0.5 line-clamp-2">{template.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-meta">
                      <span>{template.sections.length} sections</span>
                      <span>·</span>
                      <span>{isCreator ? 'You' : template.creatorName}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          {/* Preview of selected template */}
          {selected && (
            <div className="border rounded-lg p-3 bg-muted/30 space-y-1.5 mt-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">Sections Preview</p>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                  {selected.sections.length} sections
                </Badge>
              </div>
              {selected.sections.map((section, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground w-4 text-right">{i + 1}.</span>
                  <span className="truncate">{section.title}</span>
                  {section.targetWordCount && (
                    <span className="text-muted-foreground ml-auto flex-shrink-0">
                      {section.targetWordCount} words
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!selected}>
            Use Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
